import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, RadioGroup } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Item {
  id: string;
  title: string;
  dibb: string;
  evidence: string;
  owner: string;
}

export default function DIBBPage() {
  const [items, setItems] = useState<Item[]>([
    { id: '1', title: '', dibb: '', evidence: '', owner: '' },
    { id: '2', title: '', dibb: '', evidence: '', owner: '' },
    { id: '3', title: '', dibb: '', evidence: '', owner: '' },
  ]);

  const updateItem = (id: string, field: keyof Item, value: string) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems([...items, { id: Date.now().toString(), title: '', dibb: '', evidence: '', owner: '' }]);
  };

  return (
    <TaskPage
      phase="gather"
      title='"Why this?" Tag (DIBB)'
      pageNumber={2}
      instruction="For each item, pick the thinnest reason it belongs. Check one: Data · Insight · Belief · Bet"
      whyItMatters="Light scaffolds (short choices + examples) free working memory for content, not bookkeeping."
      coachRail={{
        timing: "10 minutes",
        tips: "Data = metric/log. Insight = pattern. Belief = informed conviction. Bet = hypothesis to test.",
        debrief: "What evidence types are we missing? Which items need stronger evidence?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">DIBB Tagging</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--hover-tint)] border-b-2 border-[var(--rule)]">
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">Title</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">D/I/B/B</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">1-line evidence or link</th>
                  <th className="p-2 text-left text-sm font-semibold text-[var(--text-sec)]">Who says/owns it</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-[var(--rule)]">
                    <td className="p-2">
                      <TextInput
                        value={item.title}
                        onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                        placeholder="Item title"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <RadioGroup
                        options={[
                          { value: 'Data', label: 'D' },
                          { value: 'Insight', label: 'I' },
                          { value: 'Belief', label: 'B' },
                          { value: 'Bet', label: 'B' }
                        ]}
                        value={item.dibb}
                        onChange={(value) => updateItem(item.id, 'dibb', value)}
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={item.evidence}
                        onChange={(e) => updateItem(item.id, 'evidence', e.target.value)}
                        placeholder="Evidence or link"
                        className="text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <TextInput
                        value={item.owner}
                        onChange={(e) => updateItem(item.id, 'owner', e.target.value)}
                        placeholder="Who says/owns"
                        className="text-sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              onClick={addItem}
              className="mt-4 w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
            >
              + Add another item
            </button>
          </div>
        </div>

        <Callout type="tip" phase="gather">
          <div className="space-y-2 text-sm">
            <p><strong>DIBB Guide:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Data:</strong> a metric or log (paste link/number)</li>
              <li><strong>Insight:</strong> a pattern from research/support/sales</li>
              <li><strong>Belief:</strong> informed conviction (name the source/person)</li>
              <li><strong>Bet:</strong> a hypothesis we want to test</li>
            </ul>
          </div>
        </Callout>
      </div>
    </TaskPage>
  );
}






